import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, BadgeCheck, Trash2, RotateCcw, Pencil, Infinity, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { logError } from "../lib/logger";

export default function AdminNetwork() {
  const [members, setMembers] = useState([]);
  const [wds, setWds] = useState([]);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("members");

  const load = useCallback(async () => {
    try {
      const [m, w, r] = await Promise.all([
        api.get("/admin/network/members"), api.get("/admin/withdrawals"), api.get("/admin/network/report"),
      ]);
      setMembers(m.data); setWds(w.data); setReport(r.data);
    } catch (e) { logError("admin network load", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grant = async (id, plan) => {
    try {
      await api.post(`/admin/users/${id}/grant-subscription`, { plan });
      toast.success(plan === "investor" ? "INVESTOR attivato (VIP annuale + Network, +365 giorni)" : "VIP attivato (+30 giorni)");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const grantLifetime = async (id, email) => {
    if (!window.confirm(`Rendere ${email} un COLLABORATORE LIFETIME? VIP + Network permanenti (senza scadenza), non paga ma può invitare e guadagnare NP/NB.`)) return;
    try {
      await api.post(`/admin/users/${id}/grant-lifetime`);
      toast.success("Collaboratore Lifetime attivato (VIP + Network permanenti)");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revocare l'abbonamento di questo utente (VIP + Network)?")) return;
    try { await api.post(`/admin/users/${id}/revoke-subscription`); toast.success("Abbonamento revocato"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const resetPlan = async (id, email) => {
    if (!window.confirm(`Riportare ${email} al piano standard (gratuito)? Verrà rimosso solo l'abbonamento VIP/INVESTOR. Network e saldi restano invariati.`)) return;
    try { await api.post(`/admin/users/${id}/reset-plan`); toast.success("Utente riportato al piano standard"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const resetBalance = async (id, email, type) => {
    if (!window.confirm(`Azzerare il saldo ${type} di ${email}? L'operazione è irreversibile e non tocca abbonamento né network.`)) return;
    try { await api.post(`/admin/users/${id}/reset-network-balance`, { type }); toast.success(`Saldo ${type} azzerato`); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const deleteUser = async (id, email) => {
    if (!window.confirm(`Eliminare definitivamente l'account ${email}? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Account eliminato"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const setSponsor = async (id, email, current) => {
    const val = window.prompt(
      `Imposta lo sponsor di ${email}.\nInserisci l'email dello sponsor (lascia vuoto per rimuoverlo):`,
      current || "");
    if (val === null) return;
    try {
      const r = await api.post(`/admin/network/${id}/set-sponsor`, { sponsor_email: val.trim() });
      toast.success(r.data.sponsor_email ? `Sponsor impostato: ${r.data.sponsor_email}` : "Sponsor rimosso");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const handleWd = async (wid, action) => {
    try { await api.post(`/admin/withdrawals/${wid}/${action}`); toast.success(action === "approve" ? "Prelievo approvato" : "Prelievo rifiutato"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const pending = wds.filter((w) => w.status === "pending");
  const q = search.trim().toLowerCase();
  const filteredMembers = q
    ? members.filter((m) => (m.email || "").toLowerCase().includes(q) || (m.name || "").toLowerCase().includes(q))
    : members;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-network-page">
      <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft size={16} /> Torna ad Admin
      </Link>
      <h1 className="text-3xl font-bold tracking-tight mb-1">Gestione Network</h1>
      <p className="text-sm text-zinc-400 mb-6">Attiva/rinnova gli abbonamenti (VIP 35€ · INVESTOR 315€) per chi paga fuori piattaforma, e gestisci i prelievi.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-members" onClick={() => setTab("members")}
          className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${tab === "members" ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`}>
          Membri ({members.length})
        </button>
        <button data-testid="tab-withdrawals" onClick={() => setTab("withdrawals")}
          className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${tab === "withdrawals" ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`}>
          Prelievi {pending.length > 0 && <span className="ml-1 text-amber-400">({pending.length})</span>}
        </button>
        <button data-testid="tab-report" onClick={() => setTab("report")}
          className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${tab === "report" ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`}>
          Guadagni per ramo
        </button>
      </div>

      {tab === "members" && (
        <div>
          <div className="mb-4 relative max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="member-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per email o nome…"
              className="w-full bg-[#141414] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30" />
          </div>
          {q && <div className="text-xs text-zinc-500 mb-2" data-testid="search-count">{filteredMembers.length} risultati per "{search}"</div>}
        <div className="bg-[#141414] border border-white/10 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase border-b border-white/10">
              <tr>
                <th className="text-left p-3">Utente</th>
                <th className="text-left p-3">Sponsor</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-left p-3">Piano</th>
                <th className="text-right p-3">NP</th>
                <th className="text-right p-3">NB</th>
                <th className="text-right p-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m) => (
                <tr key={m.user_id} data-testid={`member-${m.user_id}`} className="border-b border-white/5">
                  <td className="p-3">
                    <div className="font-medium">{m.name || "—"}</div>
                    <div className="text-xs text-zinc-500">{m.email}</div>
                  </td>
                  <td className="p-3 text-zinc-400">
                    <button data-testid={`set-sponsor-${m.user_id}`} onClick={() => setSponsor(m.user_id, m.email, m.sponsor_email)}
                      className="inline-flex items-center gap-1.5 hover:text-white transition-colors underline decoration-dotted decoration-zinc-600 underline-offset-4" title="Modifica sponsor">
                      <span>{m.sponsor_email || "— (imposta)"}</span>
                      <Pencil size={12} className="text-amber-400" />
                    </button>
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm ${m.network_active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-zinc-500"}`}>
                      {m.network_active ? "Attivo" : "Non attivo"}
                    </span>
                  </td>
                  <td className="p-3" data-testid={`plan-${m.user_id}`}>
                    {m.lifetime ? (
                      <span className="text-xs inline-flex items-center gap-1 text-emerald-400 font-medium">
                        <Infinity size={13} /> LIFETIME
                      </span>
                    ) : (m.subscription_plan || m.network_active) ? (
                      <span className={`text-xs ${(m.days_remaining ?? 0) > 0 ? "text-zinc-300" : "text-red-400"}`}>
                        <span className={m.subscription_plan === "vip_monthly" ? "text-amber-400" : "text-blue-400"}>
                          {m.subscription_plan === "vip_monthly" ? "VIP" : "INVESTOR"}
                        </span>
                        {" · "}
                        {(m.days_remaining ?? 0) > 0 ? `${m.days_remaining}g rimasti` : "scaduto"}
                      </span>
                    ) : <span className="text-xs text-zinc-600">—</span>}
                  </td>
                  <td className="p-3 text-right font-mono">
                    <div className="flex items-center justify-end gap-1.5">
                      <span>{m.np_balance}</span>
                      <button data-testid={`reset-np-${m.user_id}`} onClick={() => resetBalance(m.user_id, m.email, "NP")} title="Azzera NP"
                        className="inline-flex items-center justify-center p-1 rounded-sm bg-white/5 text-zinc-500 hover:bg-red-500/15 hover:text-red-400">
                        <RotateCcw size={11} />
                      </button>
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">
                    <div className="flex items-center justify-end gap-1.5">
                      <span>{m.nb_balance}</span>
                      <button data-testid={`reset-nb-${m.user_id}`} onClick={() => resetBalance(m.user_id, m.email, "NB")} title="Azzera NB"
                        className="inline-flex items-center justify-center p-1 rounded-sm bg-white/5 text-zinc-500 hover:bg-red-500/15 hover:text-red-400">
                        <RotateCcw size={11} />
                      </button>
                    </div>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`grant-vip-${m.user_id}`} onClick={() => grant(m.user_id, "vip")}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 mr-2">
                      <BadgeCheck size={13} /> VIP 35€
                    </button>
                    <button data-testid={`grant-investor-${m.user_id}`} onClick={() => grant(m.user_id, "investor")}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 mr-2">
                      <BadgeCheck size={13} /> INVESTOR 315€
                    </button>
                    <button data-testid={`grant-lifetime-${m.user_id}`} onClick={() => grantLifetime(m.user_id, m.email)} title="Collaboratore Lifetime (VIP + Network permanenti, gratis)"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 mr-2">
                      <Infinity size={13} /> Lifetime
                    </button>
                    {m.network_active && (
                      <button data-testid={`revoke-${m.user_id}`} onClick={() => revoke(m.user_id)}
                        className="text-xs px-2 py-1 rounded-sm bg-white/5 text-zinc-400 hover:bg-red-500/15 hover:text-red-400 mr-2">Revoca</button>
                    )}
                    <button data-testid={`reset-plan-${m.user_id}`} onClick={() => resetPlan(m.user_id, m.email)} title="Reset piano standard"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-white/5 text-zinc-400 hover:bg-amber-500/15 hover:text-amber-400 mr-2">
                      <RotateCcw size={13} /> Reset
                    </button>
                    <button data-testid={`delete-${m.user_id}`} onClick={() => deleteUser(m.user_id, m.email)} title="Elimina account"
                      className="inline-flex items-center justify-center text-xs p-1.5 rounded-sm bg-white/5 text-zinc-400 hover:bg-red-500/20 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {tab === "withdrawals" && (
        <div className="space-y-2">
          {wds.length === 0 && <div className="text-sm text-zinc-600">Nessuna richiesta di prelievo.</div>}
          {wds.map((w) => (
            <div key={w.id} data-testid={`admin-wd-${w.id}`} className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-md px-4 py-3">
              <div>
                <div className="font-medium text-sm">{w.name || w.email} · {w.type} · {w.amount_eur}€</div>
                <div className="text-xs text-zinc-500">{w.email} · {w.points} punti · {new Date(w.created_at).toLocaleString("it-IT")}</div>
              </div>
              {w.status === "pending" ? (
                <div className="flex gap-2">
                  <button data-testid={`approve-${w.id}`} onClick={() => handleWd(w.id, "approve")}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"><CheckCircle size={14} /> Approva</button>
                  <button data-testid={`reject-${w.id}`} onClick={() => handleWd(w.id, "reject")}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm bg-red-500/10 text-red-400 hover:bg-red-500/20"><XCircle size={14} /> Rifiuta</button>
                </div>
              ) : (
                <span className={`text-xs px-2 py-1 rounded-sm ${w.status === "approved" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                  {w.status === "approved" ? "Approvato" : "Rifiutato"}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "report" && report && (
        <div data-testid="report-view">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-[#141414] border border-white/10 rounded-md p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">NP totali rete</div>
              <div className="text-2xl font-bold text-emerald-400 mt-1">{report.total_np}</div>
              <div className="text-xs text-zinc-600">≈ {(report.total_np * (report.np_eur || 1)).toFixed(2)}€</div>
            </div>
            <div className="bg-[#141414] border border-white/10 rounded-md p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">NB totali rete</div>
              <div className="text-2xl font-bold text-purple-400 mt-1">{report.total_nb}</div>
            </div>
            <div className="bg-[#141414] border border-white/10 rounded-md p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Valore totale</div>
              <div className="text-2xl font-bold text-white mt-1">{report.total_value_eur}€</div>
            </div>
            <div className="bg-[#141414] border border-white/10 rounded-md p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Rami diretti</div>
              <div className="text-2xl font-bold text-white mt-1">{report.branches.length}</div>
              <div className="text-xs text-zinc-600 truncate">root: {report.root_email || "—"}</div>
            </div>
          </div>
          <div className="bg-[#141414] border border-white/10 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 uppercase border-b border-white/10">
                <tr>
                  <th className="text-left p-3">Ramo (diretto)</th>
                  <th className="text-right p-3">Membri</th>
                  <th className="text-right p-3">Attivi</th>
                  <th className="text-right p-3">NP ramo</th>
                  <th className="text-right p-3">NB ramo</th>
                  <th className="text-right p-3">Valore €</th>
                </tr>
              </thead>
              <tbody>
                {report.branches.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-zinc-600">Nessun ramo diretto sotto la root.</td></tr>
                )}
                {report.branches.map((b) => (
                  <tr key={b.head_email} data-testid={`branch-${b.head_email}`} className="border-b border-white/5">
                    <td className="p-3">
                      <div className="font-medium flex items-center gap-2">
                        {b.head_name || b.head_email}
                        <span className={`w-2 h-2 rounded-full ${b.head_active ? "bg-emerald-400" : "bg-zinc-600"}`} />
                      </div>
                      <div className="text-xs text-zinc-500">{b.head_email}</div>
                    </td>
                    <td className="p-3 text-right font-mono">{b.members}</td>
                    <td className="p-3 text-right font-mono text-emerald-400">{b.active_members}</td>
                    <td className="p-3 text-right font-mono">{b.np_total}</td>
                    <td className="p-3 text-right font-mono">{b.nb_total}</td>
                    <td className="p-3 text-right font-mono text-white">{(b.np_value_eur + b.nb_value_eur).toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
